import { Box, Typography } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { CustomSelect, CustomTextField } from "../../../../shared/components/inputfields/form-elements";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { EditableColumnDef, EditableTable } from "../../../../shared/components/table/inline-edit-table";

import { Utills } from "../../../../shared/app-configs/utilit";
import { OptionType } from "../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";

import { initATRawMaterialInfo } from "../../atc-models/atc-initial-values";
import { ATRawMaterialInfo } from "../../atc-models/atc-org";
import { atcService } from "../../atc-service/atc.service";

const WEIGHT_TOLERANCE = 0.01; // 0.01 units (same idea as TOLERANCE_CENTS=2)
// -------------------------------
// Validation Schema
// -------------------------------
const validationSchema = Yup.object().shape({
  rawMaterialCode: Yup.string().required("Raw Material Code is required"),
  rawMaterialComponentCode: Yup.string().required("Raw Material Component is required"),
  rawMaterialWeight: Yup.number()
    .typeError("Please enter a valid number")
    .min(0, "Must be greater than or equal to 0")
    .optional(),
});

// -------------------------------
// Props
// -------------------------------
interface RawMaterialInformationTabProps {
  onNextTab?: () => Promise<void>;
  scStatus?: boolean;
  formRef?: React.Ref<FormikProps<ATRawMaterialInfo>>;
  detailsData?: ATRawMaterialInfo[];
  setDetailsData?: React.Dispatch<React.SetStateAction<ATRawMaterialInfo[]>>;
  productNumbers?: any
  productWeightsMap?: Record<string, number>;
  selectedStandards?: string[];
}

// -------------------------------
// Component
// -------------------------------
const RawMaterialInformationForm = forwardRef<FormikProps<ATRawMaterialInfo> & { hasUnsavedRow: boolean }, RawMaterialInformationTabProps>(
  ({ scStatus, detailsData, setDetailsData, productNumbers, productWeightsMap, selectedStandards = [] }, ref) => {
    const { formtype} = useParams();
    const { notify } = useNotification();
    const { setRestrictPopUpSave } = useTransactionCertificateStore();

    // -------------------------------
    // Local State
    // -------------------------------
    const [data, setData] = useState<ATRawMaterialInfo[]>(detailsData || []);
    const [tableData, setTableData] = useState<ATRawMaterialInfo[]>(detailsData || []);
    const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const hasSubmitSC: boolean = formtype === "submited-sc";
    const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;

    const [rawMaterialCodeOptions, setRawMaterialCodeOptions] = useState<OptionType[]>([]);
    const selectedStandardsKey = selectedStandards
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(",");


    useEffect(() => {
      const getCountry = async () => {
        try {
          setIsLoading(true)
          const result: any = await atcService.getCountry();
          const countries = result.data.map((t: any) => ({
            value: t.code_1,
            label: `${t.description_1} (${t.code_1})`,
          }));
          setCountryOptions(countries);
        } catch (err) {
          notify("Failed to fetch countries", "error");
          setIsLoading(false)
        }finally{
          setIsLoading(false)
        }
      };

     
    getCountry()
    },[])

    const getState = async (countryCode: string) => {
      try {
        const result: any = await atcService.getStateBasedonCountry(
          countryCode
        );
        const states = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setStateOptions(states);
      } catch (err) {
        setStateOptions([]);
        setIsLoading(false)
      }finally{
        setIsLoading(false)
      }
    };

    // -------------------------------
    // Formik Setup
    // -------------------------------
    const formik = useFormik<ATRawMaterialInfo>({
      initialValues: initATRawMaterialInfo,
      validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: () => {},
    });

    useImperativeHandle(
      ref,
      () => Object.assign(formik, { hasUnsavedRow: editingRowIndex !== -1 }),
      [formik, editingRowIndex]
    );

    // Keep parent state in sync (only commit complete rows)
    useEffect(() => {
      if (setDetailsData) {
        const isComplete = (row: ATRawMaterialInfo) =>
          row.associatedProductNumber !== undefined &&
          row.associatedProductNumber !== "" &&
          row.rawMaterialCode !== undefined &&
          row.rawMaterialCode !== "" &&
          row.rawMaterialComposition !== undefined &&
          row.rawMaterialComposition !== "";
        // Only set complete rows after a save action, not on every change
        // This assumes handleSaveRow already updates setData and setDetailsData
        setDetailsData(data.filter(isComplete));
      }
    }, [data, setDetailsData]); // Add setDetailsData as dependency to ensure it runs when changed
    // -------------------------------
    // Handlers
    // -------------------------------
    const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) => (index === rowIndex ? { ...row, [columnId]: value } : row))
      );
    };

    const handleAddNewRow = () => {
      // Start with a clean, empty row (no default selections or values)
      const newRow: ATRawMaterialInfo = { ...initATRawMaterialInfo };
      setData((prevData) => [...prevData, newRow]);
      setEditingRowIndex(data.length);
      formik.setValues(newRow);
      setRestrictPopUpSave(true);
    };

    const handleEditRow = (rowIndex: number) => {
      setEditingRowIndex(rowIndex);
      formik.setValues(data[rowIndex]);
      setRestrictPopUpSave(true);
    };

    const handleCancelEdit = () => {
      setData(tableData);
      setEditingRowIndex(-1);
      formik.resetForm();
      setRestrictPopUpSave(false);
    };

    const handleSaveRow = (updatedRow: ATRawMaterialInfo) => {
      const { associatedProductNumber, rawMaterialComposition, rawMaterialWeight } = updatedRow;
      const certifiedWeight = Number(productWeightsMap?.[associatedProductNumber] || 0);
    
      if (!associatedProductNumber || !updatedRow.rawMaterialCode || !rawMaterialComposition) {
        notify("Please fill all required fields", "error");
        return;
      }

    
      const intComposition = Number(rawMaterialComposition) || 0;
      const intWeight = Number(rawMaterialWeight) || 0;
    
      // Calculate remaining values excluding the current row
      const { remainingComposition, remainingWeight } = getRemainingValues(associatedProductNumber, editingRowIndex);
    
      // Check if the new row's values fit within the remaining amounts
      if (intComposition > remainingComposition) {
        notify(`Composition cannot exceed ${remainingComposition}% for product ${associatedProductNumber}`, "error");
        return;
      }
      // Allow a tiny overshoot up to WEIGHT_TOLERANCE, and snap it down if needed
      const weightDiff = intWeight - remainingWeight;

      if (weightDiff > WEIGHT_TOLERANCE) {
        // Clearly too big – real user error
        notify(
          `Weight cannot exceed ${remainingWeight.toFixed(2)} for product ${associatedProductNumber}`,
          "error"
        );
        return;
      }

      // If we’re only slightly over due to floating rounding, clamp to remainingWeight
      if (weightDiff > 0 && weightDiff <= WEIGHT_TOLERANCE) {
        updatedRow.rawMaterialWeight = remainingWeight.toFixed(2);
      }
    
      // Calculate total weight only for the specific product, excluding the editing row
      const existingWeights = data
        .filter(row => row.associatedProductNumber === associatedProductNumber)
        .filter(row => editingRowIndex === -1 || row !== data[editingRowIndex]) // Exclude the row being edited
        .reduce((sum, row) => sum + (Number(row.rawMaterialWeight) || 0), 0);
      const totalWeightUsed = existingWeights + intWeight;
      console.log("Debug - Individual Weights:", data
        .filter(row => row.associatedProductNumber === associatedProductNumber)
        .map(row => ({ product: row.associatedProductNumber, weight: Number(row.rawMaterialWeight) || 0 })));
      console.log("Debug - Existing Weights Sum:", existingWeights, "New Row Weight:", intWeight, "Total Weight Used:", totalWeightUsed, "Certified Weight:", certifiedWeight);
    
      const totalDiff = totalWeightUsed - certifiedWeight;

      if (totalDiff > WEIGHT_TOLERANCE) {
        notify(
          `Total weight cannot exceed ${certifiedWeight.toFixed(2)} for product ${associatedProductNumber}`,
          "error"
        );
        return;
      }

      // If slightly over due to rounding, clamp the updated row
      if (totalDiff > 0 && totalDiff <= WEIGHT_TOLERANCE) {
        updatedRow.rawMaterialWeight = (
          intWeight - totalDiff
        ).toFixed(2);
      }

    
      setData((prevData) => {
        const newData = [...prevData];
        if (editingRowIndex >= 0) {
          newData[editingRowIndex] = updatedRow;
        } else {
          newData.push(updatedRow);
        }
        return newData;
      });
    
      setTableData((prev) => [...data]);
      setEditingRowIndex(-1);
      setRestrictPopUpSave(false);
      formik.resetForm();
    };
    

    const handleDeleteRow = (rowIndex: number) => {
        const updatedData = data.filter((_, index) => index !== rowIndex);
        setData(updatedData);
        setTableData(updatedData);
    };

// ----------------Helper Functions----------
const getRemainingValues = (productNumber: string, rowIndex?: number) => {
  const totalCompositionUsed = data.reduce((sum, row, idx) => {
    if (idx === rowIndex) return sum; // exclude current editing row
    if (row.associatedProductNumber === productNumber) {
      return sum + (Number(row.rawMaterialComposition) || 0);
    }
    return sum;
  }, 0);

  const totalWeightUsed = data.reduce((sum, row, idx) => {
    if (idx === rowIndex) return sum;
    if (row.associatedProductNumber === productNumber) {
      return sum + (Number(row.rawMaterialWeight) || 0);
    }
    return sum;
  }, 0);

  const certifiedWeight = Number(productWeightsMap?.[productNumber] || 0);

  return {
    remainingComposition: 100 - totalCompositionUsed,
    remainingWeight: certifiedWeight - totalWeightUsed,
  };
};

// filter products for dropdown
const getAvailableProducts = () => {
  if (!productNumbers) return [];

  return productNumbers
    .filter((p: string) => {
      const { remainingComposition, remainingWeight } = getRemainingValues(p, editingRowIndex);

      // ✅ Include products that have remaining composition OR weight, or are being edited
      return remainingComposition > 0 || remainingWeight > 0;
    })
    .map((num: string) => ({ label: num.toString(), value: num }));
};

    // -------------------------------
    // Table Config
    // -------------------------------

    const columns: EditableColumnDef<ATRawMaterialInfo>[] = [
      {
        accessorKey: "associatedProductNumber",
        header: "Associated Product Article Number",
        Header: (
          <span>
           <i style={{ color: "red" }}>*</i> Associated Product Article Number 
          </span>
        ),
        renderEditCell: ({ value, onChange }) => (
          <CustomSelect
            size="small"
            value={value ?? ""}
            placeholder="Select Product"
            options={getAvailableProducts()}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
          />
        )
        ,
      },
      {
        accessorKey: "rawMaterialCode",
        header: "Raw Material Code",
        Header: (
          <span>
            <i style={{ color: "red" }}>*</i> Raw Material Code 
          </span>
        ),
        renderEditCell: ({ value, onChange }) => (
          <CustomSelect
            size="small"
            value={value ?? ""}
            placeholder="Select Code"
            options={rawMaterialCodeOptions}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            enableSearch={true}
          />
        ),
      },
      {
        accessorKey: "rawMaterialComposition",
        header: "Raw Material Composition %",
        Header: (
          <span>
            <i style={{ color: "red" }}>*</i> Raw Material Composition %
          </span>
        ),
        renderEditCell: ({ value, onChange, rowIndex }) => (
          <CustomTextField
            size="small"
            value={value ?? ""}
            placeholder="Enter Composition"
            onChange={(e) => {
              let cleaned = e.target.value.replace(/[^0-9.]/g, ""); // keep digits + dot

              // keep only the first dot
              const firstDot = cleaned.indexOf(".");
              if (firstDot !== -1) {
                cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
              }

              // limit integer part to 3 digits and decimals to 2 places
              if (cleaned.includes(".")) {
                const [intPart, decPart] = cleaned.split(".");
                cleaned = intPart.slice(0, 3) + "." + (decPart ?? "").slice(0, 2);
              } else {
                cleaned = cleaned.slice(0, 3);
              }

              if (cleaned === "") {
                onChange(cleaned);
                return;
              }

              const enteredValue = cleaned;
              const productNumber = data[rowIndex]?.associatedProductNumber;
              if (!productNumber) {
                notify("Please select a product first", "error");
                return;
              }

              // sum composition for the same product (as float)
              const totalOtherComposition = data.reduce((sum, row, idx) => {
                if (idx === rowIndex) return sum;
                if (row.associatedProductNumber === productNumber) {
                  return sum + (parseFloat(String(row.rawMaterialComposition)) || 0);
                }
                return sum;
              }, 0);

              const totalAfterUpdate = totalOtherComposition + (parseFloat(enteredValue) || 0);
              if (totalAfterUpdate > 100) {
                notify(`Composition cannot exceed 100% for product ${productNumber}. Remaining allowed: ${Math.max(0, 100 - totalOtherComposition)}%`, "error");
                return;
              }

              const productWeight = Number(productWeightsMap?.[productNumber] || 0);
              const autoWeight = ((productWeight * (parseFloat(enteredValue) || 0)) / 100).toFixed(2);

              setData((prevData) =>
                prevData.map((row, idx) =>
                  idx === rowIndex
                    ? { ...row, rawMaterialComposition: enteredValue, rawMaterialWeight: autoWeight }
                    : row
                )
              );
              onChange(enteredValue);
            }}
            sx={{ mb: 2 }}
            fullWidth
          />
        ),
      }
      ,      
      {
        accessorKey: "countryArea",
        header: "Country/Area",
        renderEditCell: ({ value, onChange, rowIndex }) => (
          <CustomSelect
            size="small"
            value={Array.isArray(value) ? value : []}
            placeholder="Select Country"
            options={countryOptions}
            onChange={(e) => {
              const selectedCountry: any = e.target.value;
      
              // Update table row data
              handleCellChange(rowIndex, "countryArea", selectedCountry);
      
              // Reset the stateProvince for this row
              handleCellChange(rowIndex, "stateProvince", []);
      
              // Fetch states for the newly selected country
              getState(selectedCountry);
            }}
            fullWidth
            enableSearch={true}
            multiple
          />
        ),
      },
      {
        accessorKey: "stateProvince",
        header: "State/Province",
        renderEditCell: ({ value, onChange }) => (
          <CustomSelect
            size="small"
            value={Array.isArray(value) ? value : []}
            placeholder="Select State"
            options={stateOptions}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            enableSearch={true}
            multiple
          />
        ),
      },
      {
        accessorKey: "rawMaterialWeight",
        header: "Certified Raw Material Weight",
        renderEditCell: ({ value }) => (
          <CustomTextField
            sx={{ mb: 2 }}
            size="small"
            value={value ?? ""}
            placeholder="Auto-calculated"
            InputProps={{ readOnly: true }} // ✅ locked field
            fullWidth
          />
        ),
      },      
    ];


    useEffect(() => {
        const getRawMaterialCodes = async () => {
      try {
        const result: any = await atcService.getRawMaterialCodes(
          selectedStandardsKey ? selectedStandardsKey.split(",") : undefined
        );
        const options = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          code: t.code_1,
        }));
        setRawMaterialCodeOptions(options);
      } catch (err) {
        Utills.cl(`Error in Fetching Raw Material Codes ${err}`);
        notify('Error fetching raw material codes', 'error');
      }
    }

    getRawMaterialCodes()
    },[notify, selectedStandardsKey])

   
    // -------------------------------
    // JSX
    // -------------------------------
    return (
      <>
        {isLoading ? (
          <LoaderComponent />
        ) : (
          <Box
          sx={{
            flexGrow: 1,
            p: 1,
            border: "2px solid rgba(128, 128, 128, .2)",
            my: 4,
          }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Raw Material Information
              </Typography>
              <StyledButton
                onClick={handleAddNewRow}
                disabled={isReadOnlyBasedOnStatus || editingRowIndex !== -1}
              >
                Add Raw Material
              </StyledButton>
            </Box>

            <EditableTable
              data={data}
              columns={columns}
              onSaveRow={handleSaveRow}
              onCellChange={handleCellChange}
              editingRowIndex={editingRowIndex}
              onEditRow={handleEditRow}
              onCancelEdit={handleCancelEdit}
              onDeleteRow={handleDeleteRow}
              enableEditing={!isReadOnlyBasedOnStatus}
              isSaving={false}
            />
          </Box>
        )}
      </>
    );
  }
);

export default RawMaterialInformationForm; 
